import {
  clamp,
  el,
  getPreviewPlan,
  getSettings,
  refreshMetrics,
  state,
} from "./core.js"
import { closeImageMap, decodeFrameImages } from "./media.js"
import {
  getCropDecodeDimensions,
  getCropRectForDecoded,
  getCroppedOutputDimensions,
  refreshCropMetrics,
} from "./crop.js"

let animationFrame = 0
let rebuildTimer = 0
let lastOutputFrame = -1
let playRequestPending = false

function installPreviewLoadingUi() {
  if (document.getElementById("previewLoading")) return
  const shell = el.livePreview?.closest(".result-shell")
  if (!shell) return
  const overlay = document.createElement("div")
  overlay.id = "previewLoading"
  overlay.hidden = true
  overlay.innerHTML = '<span class="preview-spinner" aria-hidden="true"></span><strong id="previewLoadingText">Processing preview…</strong><small id="previewLoadingCount"></small>'
  shell.appendChild(overlay)
  const style = document.createElement("style")
  style.id = "previewLoadingStyles"
  style.textContent = `
    #previewLoading{position:absolute;z-index:20;inset:0;display:grid;place-content:center;justify-items:center;gap:8px;padding:18px;background:rgba(2,6,9,.82);backdrop-filter:blur(4px);text-align:center}
    #previewLoading[hidden]{display:none!important}
    #previewLoading strong{font-size:.8rem}
    #previewLoading small{color:rgba(235,244,249,.68);font-size:.72rem}
    .preview-spinner{width:30px;height:30px;border:3px solid rgba(255,255,255,.16);border-top-color:#52e6ff;border-radius:50%;animation:preview-spin .7s linear infinite}
    @keyframes preview-spin{to{transform:rotate(360deg)}}
  `
  document.head.appendChild(style)
}

function setPreviewLoading(visible, phase = "Processing preview…", done = null, total = null) {
  installPreviewLoadingUi()
  const overlay = document.getElementById("previewLoading")
  const text = document.getElementById("previewLoadingText")
  const count = document.getElementById("previewLoadingCount")
  if (!overlay) return
  overlay.hidden = !visible
  if (text) text.textContent = phase
  if (count) count.textContent = done == null || total == null ? "" : `${done}/${total}`
}

function createSourceCanvas() {
  if (state.sourceCanvas) return state.sourceCanvas
  const canvas = document.createElement("canvas")
  canvas.id = "sourceFrameCanvas"
  canvas.hidden = true
  canvas.setAttribute("aria-label", "Buffered reverse and rebound preview")
  canvas.style.cssText = "display:block;width:100%;height:auto;max-height:70vh;background:#000;object-fit:contain;"
  el.videoPreview.after(canvas)
  state.sourceCanvas = canvas
  return canvas
}

function setCanvasSize(canvas, width, height) {
  if (canvas.width !== width) canvas.width = width
  if (canvas.height !== height) canvas.height = height
}

function showNativeSource() {
  el.videoPreview.hidden = false
  if (state.sourceCanvas) state.sourceCanvas.hidden = true
}

function showBufferedSource() {
  el.videoPreview.hidden = true
  createSourceCanvas().hidden = false
}

function approximateGifPalette(context, width, height, colors) {
  if (colors >= 224) return
  const image = context.getImageData(0, 0, width, height)
  const levels = Math.max(2, Math.round(Math.cbrt(colors)))
  const step = 255 / (levels - 1)
  for (let index = 0; index < image.data.length; index += 4) {
    image.data[index] = Math.round(image.data[index] / step) * step
    image.data[index + 1] = Math.round(image.data[index + 1] / step) * step
    image.data[index + 2] = Math.round(image.data[index + 2] / step) * step
  }
  context.putImageData(image, 0, 0)
}

function previewDimensions() {
  const settings = getSettings()
  const output = getCroppedOutputDimensions(settings)
  const maxEdge = 480
  const ratio = Math.min(1, maxEdge / Math.max(1, output.width, output.height))
  return {
    width: Math.max(1, Math.round(output.width * ratio)),
    height: Math.max(1, Math.round(output.height * ratio)),
  }
}

async function detachCanvas(canvas) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(canvas)
    } catch {
      // Canvas fallback below.
    }
  }
  const copy = document.createElement("canvas")
  copy.width = canvas.width
  copy.height = canvas.height
  copy.getContext("2d", { alpha: false }).drawImage(canvas, 0, 0)
  return copy
}

async function renderPreviewFrames(rawImages, frames, outputDimensions, decodeDimensions, settings, token) {
  const rendered = []
  const crop = getCropRectForDecoded(decodeDimensions)
  for (let index = 0; index < frames.length; index += 1) {
    if (token !== state.previewBuildToken) throw new DOMException("Preview superseded.", "AbortError")
    const spec = frames[index]
    const from = rawImages.get(spec.from)
    const to = rawImages.get(spec.to)
    if (!from || !to) throw new Error(`Missing preview source frame ${spec.from + 1}.`)

    const canvas = document.createElement("canvas")
    canvas.width = outputDimensions.width
    canvas.height = outputDimensions.height
    const context = canvas.getContext("2d", {
      alpha: false,
      willReadFrequently: settings.format === "gif" && settings.colors < 224,
    })
    context.fillStyle = "#000"
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.imageSmoothingEnabled = true
    context.imageSmoothingQuality = "medium"
    context.filter = `brightness(${settings.brightness}%) saturate(${settings.saturation}%) contrast(${settings.contrast}%) grayscale(${settings.grayscale}%)`
    context.globalAlpha = 1
    context.drawImage(from, crop.x, crop.y, crop.width, crop.height, 0, 0, canvas.width, canvas.height)
    if (spec.mix > 0 && spec.to !== spec.from) {
      context.globalAlpha = spec.mix
      context.drawImage(to, crop.x, crop.y, crop.width, crop.height, 0, 0, canvas.width, canvas.height)
    }
    context.globalAlpha = 1
    context.filter = "none"
    if (settings.format === "gif") approximateGifPalette(context, canvas.width, canvas.height, settings.colors)
    rendered.push({ image: await detachCanvas(canvas), spec })
    setPreviewLoading(true, "Rendering output preview", index + 1, frames.length)
    if (index % 8 === 0) await new Promise(requestAnimationFrame)
  }
  return rendered
}

function renderTimelineThumbnails(rawImages, uniqueIndices, decodeDimensions) {
  if (!uniqueIndices.length) return
  const crop = getCropRectForDecoded(decodeDimensions)
  const count = Math.min(12, uniqueIndices.length)
  const fragment = document.createDocumentFragment()
  for (let index = 0; index < count; index += 1) {
    const sourceIndex = uniqueIndices[Math.round(index * (uniqueIndices.length - 1) / Math.max(1, count - 1))]
    const image = rawImages.get(sourceIndex)
    if (!image) continue
    const canvas = document.createElement("canvas")
    canvas.className = "timeline-thumb"
    canvas.width = 120
    canvas.height = Math.max(48, Math.round(120 * crop.height / crop.width))
    canvas.getContext("2d", { alpha: false }).drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, canvas.width, canvas.height)
    fragment.appendChild(canvas)
  }
  if (fragment.childNodes.length) el.timelineThumbnails.replaceChildren(fragment)
}

function disposePreview(preview) {
  for (const frame of preview?.frames || []) frame.image?.close?.()
}

export async function rebuildLoopingPreview() {
  if (!state.file || !state.videoTrack || state.encoding) return
  const token = ++state.previewBuildToken
  const settings = getSettings()
  const plan = getPreviewPlan(settings)
  const outputDimensions = previewDimensions()
  const decodeDimensions = getCropDecodeDimensions(outputDimensions)
  const uniqueIndices = [...new Set(plan.frames.flatMap((frame) => [frame.from, frame.to]))].sort((a, b) => a - b)
  if (!uniqueIndices.length) return

  setPreviewLoading(true, "Decoding preview frames", 0, uniqueIndices.length)
  refreshMetrics(`Building looping preview… 0/${uniqueIndices.length}`)
  refreshCropMetrics()
  let rawImages = null
  let renderedFrames = null
  try {
    rawImages = await decodeFrameImages(uniqueIndices, decodeDimensions, {
      isCancelled: () => token !== state.previewBuildToken,
      onProgress: (done, total) => {
        if (token !== state.previewBuildToken) return
        setPreviewLoading(true, "Decoding preview frames", done, total)
        el.statusText.textContent = `Decoding preview source frames… ${done}/${total}`
      },
    })
    renderTimelineThumbnails(rawImages, uniqueIndices, decodeDimensions)
    setPreviewLoading(true, "Rendering output preview", 0, plan.frames.length)
    renderedFrames = await renderPreviewFrames(rawImages, plan.frames, outputDimensions, decodeDimensions, settings, token)
    closeImageMap(rawImages)
    rawImages = null
    if (token !== state.previewBuildToken) throw new DOMException("Preview superseded.", "AbortError")

    const oldPreview = state.preview
    state.preview = {
      frames: renderedFrames,
      width: outputDimensions.width,
      height: outputDimensions.height,
      frameDuration: Math.max(0.01, plan.previewFrameDuration),
      totalDuration: Math.max(0.01, plan.outputDuration),
      startedAt: performance.now(),
      pausedOffset: 0,
      dispose() { disposePreview(this) },
    }
    renderedFrames = null
    disposePreview(oldPreview)
    lastOutputFrame = -1
    el.livePreview.hidden = false
    el.emptyResult.hidden = true
    setPreviewLoading(false)
    refreshCropMetrics()
    refreshMetrics(plan.previewWasReduced
      ? `Looping preview ready · ${plan.frames.length} buffered frames represent ${plan.outputDuration.toFixed(2)} s`
      : null)
  } catch (error) {
    closeImageMap(rawImages)
    if (renderedFrames) disposePreview({ frames: renderedFrames })
    if (error?.name !== "AbortError" && token === state.previewBuildToken) {
      setPreviewLoading(false)
      throw error
    }
  } finally {
    if (token === state.previewBuildToken) setPreviewLoading(false)
  }
}

export function schedulePreviewRebuild(delay = 180) {
  clearTimeout(rebuildTimer)
  if (state.file && state.videoTrack) setPreviewLoading(true, "Waiting to rebuild preview")
  rebuildTimer = setTimeout(() => {
    void rebuildLoopingPreview().catch((error) => {
      setPreviewLoading(false)
      el.statusText.textContent = error instanceof Error ? error.message : String(error)
      el.errorBox.textContent = el.statusText.textContent
      el.errorBox.hidden = false
    })
  }, delay)
}

function drawOutputFrame(index, drawSource) {
  const preview = state.preview
  if (!preview || index < 0 || index >= preview.frames.length) return
  const frame = preview.frames[index]

  setCanvasSize(el.livePreview, preview.width, preview.height)
  const liveContext = el.livePreview.getContext("2d", { alpha: false })
  liveContext.clearRect(0, 0, el.livePreview.width, el.livePreview.height)
  liveContext.drawImage(frame.image, 0, 0, el.livePreview.width, el.livePreview.height)

  if (drawSource) {
    const sourceCanvas = createSourceCanvas()
    setCanvasSize(sourceCanvas, preview.width, preview.height)
    const sourceContext = sourceCanvas.getContext("2d", { alpha: false })
    sourceContext.clearRect(0, 0, sourceCanvas.width, sourceCanvas.height)
    sourceContext.drawImage(frame.image, 0, 0, sourceCanvas.width, sourceCanvas.height)
    const fromTime = state.sourceFrameTimestamps[frame.spec.from] || state.trimStart
    const toTime = state.sourceFrameTimestamps[frame.spec.to] || fromTime
    state.previewCurrentTime = fromTime + (toTime - fromTime) * frame.spec.mix
    dispatchEvent(new CustomEvent("converter-preview-time", { detail: { time: state.previewCurrentTime } }))
  }
}

function requestNativePlay() {
  if (playRequestPending || !state.previewPlaying || state.direction !== "forward") return
  const video = el.videoPreview
  if (!video.src || !video.paused) return
  playRequestPending = true
  video.play().catch(() => {}).finally(() => { playRequestPending = false })
}

function updateNativeSource() {
  const video = el.videoPreview
  showNativeSource()
  video.playbackRate = state.speed
  if (!state.previewPlaying) {
    if (!video.paused) video.pause()
  } else {
    if (video.currentTime < state.trimStart || video.currentTime >= state.trimEnd - 0.008) video.currentTime = state.trimStart
    requestNativePlay()
  }
  state.previewCurrentTime = clamp(video.currentTime || state.trimStart, state.trimStart, state.trimEnd)
  dispatchEvent(new CustomEvent("converter-preview-time", { detail: { time: state.previewCurrentTime } }))
}

function playbackLoop(now) {
  animationFrame = requestAnimationFrame(playbackLoop)
  if (state.direction === "forward") updateNativeSource()
  else {
    el.videoPreview.pause()
    showBufferedSource()
  }

  const preview = state.preview
  if (!preview?.frames.length) return
  let elapsed = preview.pausedOffset
  if (state.previewPlaying) elapsed = Math.max(0, (now - preview.startedAt) / 1000)
  const position = preview.totalDuration > 0 ? elapsed % preview.totalDuration : 0
  const animationDuration = preview.frameDuration * preview.frames.length
  const index = position >= animationDuration
    ? preview.frames.length - 1
    : clamp(Math.floor(position / preview.frameDuration), 0, preview.frames.length - 1)
  if (index !== lastOutputFrame) {
    lastOutputFrame = index
    drawOutputFrame(index, state.direction !== "forward")
  }
}

export function togglePreviewPlayback() {
  const preview = state.preview
  if (state.previewPlaying) {
    if (preview) preview.pausedOffset = Math.max(0, (performance.now() - preview.startedAt) / 1000) % Math.max(0.001, preview.totalDuration)
    state.previewPlaying = false
    el.videoPreview.pause()
  } else {
    if (preview) preview.startedAt = performance.now() - preview.pausedOffset * 1000
    state.previewPlaying = true
    requestNativePlay()
  }
  el.previewToggle.textContent = state.previewPlaying ? "❚❚" : "▶"
  el.previewToggle.setAttribute("aria-label", state.previewPlaying ? "Pause preview" : "Play preview")
}

export function seekPreviewToSourceTime(time) {
  const target = clamp(time, state.trimStart, Math.max(state.trimStart, state.trimEnd - 0.001))
  if (Number.isFinite(el.videoPreview.duration)) el.videoPreview.currentTime = target
  const preview = state.preview
  if (!preview?.frames.length) return
  let bestPosition = 0
  let bestDistance = Infinity
  for (let index = 0; index < preview.frames.length; index += 1) {
    const spec = preview.frames[index].spec
    const fromTime = state.sourceFrameTimestamps[spec.from] || 0
    const toTime = state.sourceFrameTimestamps[spec.to] || fromTime
    const sourceTime = fromTime + (toTime - fromTime) * spec.mix
    const distance = Math.abs(sourceTime - target)
    if (distance < bestDistance) {
      bestDistance = distance
      bestPosition = index
    }
  }
  preview.pausedOffset = bestPosition * preview.frameDuration
  preview.startedAt = performance.now() - preview.pausedOffset * 1000
  lastOutputFrame = -1
  drawOutputFrame(bestPosition, state.direction !== "forward")
}

export function setupPreviewEngine() {
  installPreviewLoadingUi()
  createSourceCanvas()
  showNativeSource()
  cancelAnimationFrame(animationFrame)
  animationFrame = requestAnimationFrame(playbackLoop)
  el.previewToggle.addEventListener("click", togglePreviewPlayback)
  addEventListener("converter-plan-change", (event) => {
    if (event.detail?.reason !== "direction") return
    if (state.direction === "forward") showNativeSource()
    else showBufferedSource()
  })
  addEventListener("converter-seek", (event) => seekPreviewToSourceTime(event.detail.time))
}

export function stopPreviewEngine() {
  clearTimeout(rebuildTimer)
  cancelAnimationFrame(animationFrame)
  state.previewBuildToken += 1
  disposePreview(state.preview)
  state.preview = null
  setPreviewLoading(false)
}
